import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Wrapper, Status } from "@googlemaps/react-wrapper";

// Redux selectors
import { selectUserInfo } from "../userInfo/userInfoSlice";
import {
  fetchAddressAction,
  fetchAddressByCoordinatesAction,
  selectAddress,
  selectAutoComplete,
  selectTrees,
  setAutoCompleteValue,
  selectWaterWays,
  selectRailRoads,
  selectHistoricMonuments,
  selectFireStations,
  selectGasStations,
} from "../address/addressSlice";
import {
  selectAIModelSelection,
  setAiModelSelection,
} from "../address/aiDetectionSlice";
import {
  fetchClimateRiskAction,
  selectClimateRisk,
} from "../risk/climateRiskSlice";
import {
  fetchPerilAction,
  selectArretePeril,
} from "../arretPeril/arretPerilSlice";

// Components
import OLMap, { OLMapRef } from "../map/OLMap";
import Icon from "../../components/Icon";
import ButtonConfirm from "../../components/ButtonConfirm/ButtonConfirm";
import { generatePdf } from "../address/data4homeAPI";
import { checkExclusionZone } from "../settings/exclusionZoneApi";
import { selectActiveTenantId } from "../activeTenant/activeTenantSlice";
import {
  exclusionCheckCleared,
  exclusionCheckFailed,
  exclusionCheckNotApplicable,
  exclusionCheckStarted,
  exclusionCheckSucceeded,
  selectExclusionCheck,
} from "../exclusionZone/exclusionCheckSlice";
import DataTabs from "./DataTabs";
import PlacesAutocomplete from "../search/PlacesAutocomplete";
import ClimateRiskList from "./ClimateRiskList";
import { COPRO_TAB_INDEX } from "./CoproTabContent";
import CoproPortfolioDrawer, { CoproPortfolioDrawerRequest, CoproPortfolioMapState } from "./CoproPortfolioDrawer";
import { CoproPortfolioMapPoint, CoproPortfolioOpenRequest, samePortfolioPoints } from "./coproPortfolio";

// Material-UI
import {
  Alert,
  Box,
  CssBaseline,
  Checkbox,
  Typography,
} from "@mui/material";

import AppBar from "../address/AppBar";

// Types
import { ClimateRiskType } from "../../types";
import { climateRiskItems, scaleLevels, scaleLevelType } from "../../constants";

// Styles
import "../address/Address.css";
import "./Search2.css";


const render = (status: Status) => {
  return <div>Loading...</div>;
};

const API_KEY = "AIzaSyDq2gssPa5aiQDHXNsQU7fP9DZd-v5pzho";

export default function Search2() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const arretePerils = useAppSelector(selectArretePeril);

  // Redux state
  const userInfo = useAppSelector(selectUserInfo);
  const activeTenantId = useAppSelector(selectActiveTenantId);
  const exclusionCheck = useAppSelector(selectExclusionCheck);
  const address = useAppSelector(selectAddress);
  const trees = useAppSelector(selectTrees);
  const waterWays = useAppSelector(selectWaterWays);
  const railRoads = useAppSelector(selectRailRoads);
  const historicMonuments = useAppSelector(selectHistoricMonuments);
  const fireStations = useAppSelector(selectFireStations);
  const gasStations = useAppSelector(selectGasStations);
  const aiDetectionSelectionState = useAppSelector(selectAIModelSelection);
  const climateRisk = useAppSelector(selectClimateRisk);
  const googleAutocomplete = useAppSelector(selectAutoComplete);


  // Local state
  const [isExportDisable, setIsExportDisable] = useState(true);
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState<ClimateRiskType | null>(null);
  const [selectedRcp, setSelectedRcp] = useState("rcp26");
  const [mapCenter, setMapCenter] = useState({ lat: 48.866667, lng: 2.333333 });
  const [mapZoom, setMapZoom] = useState(10);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCoproIndex, setSelectedCoproIndex] = useState(0);
  const [portfolioRequest, setPortfolioRequest] = useState<CoproPortfolioDrawerRequest | null>(null);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [portfolioPoints, setPortfolioPoints] = useState<CoproPortfolioMapPoint[]>([]);
  const [portfolioSelectedId, setPortfolioSelectedId] = useState<string | null>(null);
  const portfolioOpenSeq = useRef(0);

  // Refs
  const mapRef = useRef<OLMapRef>(null);
  /** Dernier placeId pour lequel on a lancé une requête (évite de relancer en boucle si la requête échoue). */
  const lastRequestedPlaceIdRef = useRef<string | null>(null);
  /** Dernières coordonnées pour lesquelles on a lancé une requête (idem). */
  const lastRequestedCoordsRef = useRef<{ lat: number; lng: number } | null>(null);

  // AI model permissions
  const [aiModelPermission, setAiModelPermission] = useState({
    pool: false,
    solarPanel: false,
    graffiti: false,
    walledWindow: false,
    buildingAge: false,
  });

  // Réinitialiser la copro sélectionnée quand l'adresse / placeCopro change
  useEffect(() => {
    setSelectedCoproIndex(0);
  }, [address.value?.placeId, address.value?.placeCopro]);

  // Le tiroir et ses points suivent la fiche affichée. Changer d'adresse, de copro ou d'onglet le ferme.
  useEffect(() => {
    setPortfolioOpen(false);
    setPortfolioPoints([]);
    setPortfolioSelectedId(null);
  }, [address.value?.placeId, address.value?.latitude, address.value?.longitude, selectedCoproIndex, selectedTab]);

  // Effects
  useEffect(() => {
    if (userInfo.value) {
      const pool = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_pool_detection" && item.scope.includes("view")).length > 0;
      const solarPanel = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_solar_panel_detection" && item.scope.includes("view")).length > 0;
      const graffiti = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_graffiti_detection" && item.scope.includes("view")).length > 0;
      const walledWindow = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_walled_window_detection" && item.scope.includes("view")).length > 0;
      const buildingAge = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_building_age_detection" && item.scope.includes("view")).length > 0;

      setAiModelPermission({ pool, solarPanel, graffiti, walledWindow, buildingAge });
    }
  }, [userInfo]);

  // Handle initial URL hash for tab selection
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      // The tab will be set by DataTabs component when blockItemList is available
      // This effect just ensures we're ready to handle hash changes
    }
  }, []);

  // Listen for hash changes globally
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        // Force a re-render to ensure DataTabs picks up the hash change
        // The actual tab selection will be handled by DataTabs component
        console.log('Hash changed to:', hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // URL is the single source of truth: ?placeId=xxx OR ?lat=...&lng=... drives one fetch
  useEffect(() => {
    const placeId = searchParams.get('placeId');
    const latParam = searchParams.get('lat');
    const lngParam = searchParams.get('lng');

    if (address.status === 'loading') return;

    if (placeId) {
      if (address.value?.placeId === placeId) return;
      // Ne pas relancer si la dernière requête pour ce placeId a échoué
      if (address.status === 'failed' && placeId === lastRequestedPlaceIdRef.current) return;
      lastRequestedPlaceIdRef.current = placeId;
      lastRequestedCoordsRef.current = null;
      dispatch(fetchAddressAction(placeId));
      return;
    }

    if (latParam != null && lngParam != null) {
      const lat = parseFloat(latParam);
      const lng = parseFloat(lngParam);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;
      const sameCoords =
        address.value?.latitude != null &&
        address.value?.longitude != null &&
        Math.abs(address.value.latitude - lat) < 1e-6 &&
        Math.abs(address.value.longitude - lng) < 1e-6;
      if (sameCoords) return;
      const lastCoords = lastRequestedCoordsRef.current;
      const sameAsLastFailed =
        address.status === 'failed' &&
        lastCoords != null &&
        Math.abs(lastCoords.lat - lat) < 1e-6 &&
        Math.abs(lastCoords.lng - lng) < 1e-6;
      if (sameAsLastFailed) return;
      lastRequestedCoordsRef.current = { lat, lng };
      lastRequestedPlaceIdRef.current = null;
      dispatch(fetchAddressByCoordinatesAction(lat, lng));
    }
  }, [searchParams, dispatch, address.status, address.value?.placeId, address.value?.latitude, address.value?.longitude]);

  useEffect(() => {
    if (address.value) {
      setMapCenter({ lat: address.value.latitude, lng: address.value.longitude });
      setMapZoom(16);
      document.title = `Vision - ${address.value.address}`;
      dispatch(fetchClimateRiskAction(address.value.placeId));
      

      
    } else {
      document.title = 'Vision - Data4Home';
    }
  }, [address, waterWays, railRoads, dispatch]);

  // Autocomplete : objet choisi par l'utilisateur, ou placeId (string) après un chargement par coordonnées.
  // Une nouvelle adresse empile l'historique. Le string ne fait que réécrire ?lat=&lng= en ?placeId=
  // (même action : clic carte ou « Ouvrir cette adresse »), sinon Retour tomberait sur l'URL intermédiaire.
  useEffect(() => {
    if (!googleAutocomplete) return;
    const fromCoordinates = typeof googleAutocomplete === 'string';
    const placeId = fromCoordinates
      ? googleAutocomplete
      : googleAutocomplete?.value?.place_id;
    if (!placeId) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('placeId') === placeId) return;

    const hash = window.location.hash;
    if (fromCoordinates) {
      if (!params.has('lat') || !params.has('lng')) return;
      navigate({ search: `?placeId=${placeId}`, hash }, { replace: true });
      return;
    }

    navigate({ search: `?placeId=${placeId}`, hash });
  }, [googleAutocomplete, navigate]);

  useEffect(() => {
    if (climateRisk.value && climateRisk.value.risks) {
      // Si aucun risque n'est sélectionné, sélectionner le premier disponible
      if (!selectedRisk) {
        const firstRiskAvailable = climateRiskItems.find(
          (risk) => getScaleLevel(risk, selectedRcp)?.num !== -1
        );
        setSelectedRisk(firstRiskAvailable || null);
      } else {
        // Si un risque est sélectionné, vérifier s'il est toujours disponible avec le RCP actuel
        // const currentRiskScaleLevel = getScaleLevel(selectedRisk, selectedRcp);
        // if (currentRiskScaleLevel?.num === -1) {
        //   // Le risque sélectionné n'est plus disponible, chercher un autre risque disponible
        //   const availableRisk = climateRiskItems.find(
        //     (risk) => getScaleLevel(risk, selectedRcp)?.num !== -1
        //   );
        //   setSelectedRisk(availableRisk || null);
        // }
      }
    }
  }, [climateRisk, selectedRcp, selectedRisk]);

  // Error handling
  useEffect(() => {
    if (address.error) {
      enqueueSnackbar("L'interrogation du serveur de recherche d'adresse a échoué", { variant: "error" });
    }
    if (address.value) {
      setIsExportDisable(false);
    }
  }, [address, enqueueSnackbar]);

  // Exclusion zone check for the displayed address and the active tenant
  useEffect(() => {
    const placeId = address.value?.placeId;
    const lat = address.value?.latitude;
    const lng = address.value?.longitude;
    const tenantId = activeTenantId ?? userInfo.value?.tenants?.[0]?.tenantId;
    if (!placeId && (lat == null || lng == null)) {
      dispatch(exclusionCheckCleared());
      return;
    }
    if (!tenantId) {
      dispatch(exclusionCheckNotApplicable());
      return;
    }

    let cancelled = false;
    dispatch(exclusionCheckStarted());
    const location = placeId ? { placeId } : { lat, lng };
    checkExclusionZone({ ...location, tenantId })
      .then((result) => {
        if (!cancelled) {
          dispatch(exclusionCheckSucceeded(result));
        }
      })
      .catch((error) => {
        console.error("Exclusion zone check failed", error);
        if (!cancelled) {
          dispatch(exclusionCheckFailed());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [address.value?.placeId, address.value?.latitude, address.value?.longitude, activeTenantId, userInfo.value?.tenants, dispatch]);

  // TODO: temporarily fetch perils when address changes
  // TODO: add a saga to fetch all datatabs content when address changes and remove them from being fetch in the tab itself to avoid fetching everytime we change the tab
  // TODO: it seems events and catnat manages to avoid fetching on displayed without the fetch being dispatched in the Search2.tsx file
  useEffect(() => {
    if (address?.value) {
      dispatch(
        fetchPerilAction(address?.value?.latitude, address?.value?.longitude)
      );
    }
  }, [address]);

  // Helper functions
  const getScaleLevel = (item: ClimateRiskType, rcp: string): scaleLevelType | undefined => {
    if (!climateRisk.value?.risks?.[item.id]?.properties) return scaleLevels[-1 as keyof typeof scaleLevels];
    
    const content = climateRisk.value.risks[item.id].properties;
    const scaleLevelByRisk: Record<string, (content: any, rcp: string) => scaleLevelType> = {
      flood: (content) => scaleLevels[content.riskLevel] || scaleLevels[-1 as keyof typeof scaleLevels],
      floodRunoff: (content) => scaleLevels[content.riskLevel] || scaleLevels[-1 as keyof typeof scaleLevels],
      drought: (content) => scaleLevels[content.riskLevel] || scaleLevels[-1 as keyof typeof scaleLevels],
      fire: (content) => scaleLevels[content.riskLevel] || scaleLevels[-1 as keyof typeof scaleLevels],
      hailStorm: (content) => scaleLevels[content.riskLevel] || scaleLevels[-1 as keyof typeof scaleLevels],
      heavyRain: (content, rcp) => scaleLevels[content[rcp]] || scaleLevels[-1 as keyof typeof scaleLevels],
      wind: (content, rcp) => scaleLevels[content[rcp]] || scaleLevels[-1 as keyof typeof scaleLevels],
      submersion: (content) => scaleLevels[(content.properties?.riskLevel ?? content.riskLevel ?? 0)] || scaleLevels[-1 as keyof typeof scaleLevels],
      coastalErosion: (content) => scaleLevels[content.riskLevel] || scaleLevels[-1 as keyof typeof scaleLevels],
    };
    
    const scaleLevelFunction = scaleLevelByRisk[item.id];
    return scaleLevelFunction ? scaleLevelFunction(content, rcp) : scaleLevels[-1 as keyof typeof scaleLevels];
  };

  const handleChange = (place: any) => {
    dispatch(setAutoCompleteValue(place));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setAiModelSelection(event.target.name, event.target.checked));
  };

  const handleExportPdf = async () => {
    if (!address.value?.placeId) {
      enqueueSnackbar("Aucune adresse sélectionnée", { variant: "error" });
      return;
    }

    try {
      setIsLoadingPDF(true);
      const response = await generatePdf(
        address.value.placeId,
        undefined,
        activeTenantId ?? userInfo.value?.tenants?.[0]?.tenantId,
      );
      if (response.status !== 200) {
        enqueueSnackbar("Erreur lors de l'exportation du PDF", { variant: "error" });
        return;
      }

      const filePDF = new Blob([response.arraybuffer], { type: "application/pdf" });
      const fileURLPdf = URL.createObjectURL(filePDF);
      const fileNameComponent = address.value.address.split(",").map((component: string) => component.trim());
      const fileName = `vision_data4home_${[fileNameComponent[0], fileNameComponent[1] || ""].join("_")}.pdf`.replace(/ /g, "_");
      const link = document.createElement("a");
      link.href = fileURLPdf;
      link.download = fileName;
      link.href = fileURLPdf;
      link.click();
      // window.open(fileURLPdf, "_blank");
    } catch (error: any) {
      if (error?.response?.status === 403) {
        enqueueSnackbar("Cette adresse est exclue des zones de souscription.", { variant: "error" });
        return;
      }
      enqueueSnackbar("Erreur lors de l'exportation du PDF", { variant: "error" });
      console.error("Erreur lors de l'exportation du PDF", error);
    } finally {
      setIsLoadingPDF(false);
    }
  };

  const handleRcpChange = (event: React.MouseEvent<HTMLElement>, newRcp: string) => {
    if (newRcp !== null) {
      setSelectedRcp(newRcp);
    }
  };

  const handleHistoricMonumentClick = () => {
    mapRef.current?.highlightHistoricMonuments();
  };

  const handleFireStationClick = () => {
    mapRef.current?.centerOnFireStation();
  };

  const handleGasStationClick = () => {
    mapRef.current?.centerOnGasStation();
  };

  const handleZoneSurveilleeClick = () => {
    // console.log('handleZoneSurveilleeClick called');

    if (!mapRef.current) {
      console.error('mapRef.current is null - map not initialized');
      return;
    }

    if (!mapRef.current.highlightZonesSurveillees) {
      console.error('highlightZonesSurveillees function not available on mapRef.current');
      console.log('Available methods:', Object.keys(mapRef.current));
      return;
    }

    // console.log('Calling highlightZonesSurveillees...');
    mapRef.current.highlightZonesSurveillees();
  };

  const handleCoordinatesSelected = (lat: number, lng: number) => {
    navigate({ search: `?lat=${lat}&lng=${lng}`, hash: window.location.hash });
  };

  const handleViewPortfolio = useCallback((request: CoproPortfolioOpenRequest) => {
    portfolioOpenSeq.current += 1;
    setPortfolioRequest({
      siret: request.siret,
      portfolio: request.portfolio,
      resetKey: portfolioOpenSeq.current,
    });
    setPortfolioOpen(true);
    setPortfolioSelectedId(null);
  }, []);

  const closePortfolioDrawer = useCallback(() => {
    mapRef.current?.restorePortfolioHomeView();
    setPortfolioOpen(false);
    setPortfolioPoints([]);
    setPortfolioSelectedId(null);
  }, []);

  const handlePortfolioMapState = useCallback((state: CoproPortfolioMapState) => {
    setPortfolioPoints((prev) => (samePortfolioPoints(prev, state.points) ? prev : state.points));
    setPortfolioSelectedId((prev) => (prev === state.selectedId ? prev : state.selectedId));
  }, []);

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100vw" }} className="Search2">
      <CssBaseline />
      
      {/* Main App Bar */}
      <AppBar
        title={t("applicationMenu.menuItem.data4home")}
        position="fixed"
        sx={{ zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
      />

      {/* Left Zone - Controls */}
      <Box
        sx={{
          width: "350px",
          marginTop: "64px",
          height: "calc(100vh - 64px)",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Empêche le débordement du conteneur parent
        }}
      >
        {/* Scrollable Content Container */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Search Input with Google Places Autocomplete */}
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
            <Wrapper apiKey={API_KEY} libraries={["places", "marker", "geometry"]} render={render}>
              <PlacesAutocomplete
                initialValue={googleAutocomplete?.label}
                onChange={handleChange}
              />
            </Wrapper>
          </Box>

          {/* Exclusion zone message */}
          {address.value && exclusionCheck.status === "done" && exclusionCheck.excluded && (
            <Box sx={{ px: 2, pt: 1 }}>
              <Alert severity="error" variant="filled">
                {exclusionCheck.message ?? "Cette adresse est exclue des zones de souscription."}
              </Alert>
            </Box>
          )}
          {address.value && exclusionCheck.status === "failed" && (
            <Box sx={{ px: 2, pt: 1 }}>
              <Alert severity="warning">
                Impossible de vérifier les zones d'exclusion de souscription. L'export PDF est désactivé.
              </Alert>
            </Box>
          )}
          {/* Address Information Section */}
          {address.value && (
            <Box sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }}>
              <Typography variant="body2" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#333", mb: 0.5 }}>
                Informations de l'adresse
              </Typography>
              
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ 
                  p: 0.5, 
                  backgroundColor: "#f8f9fa", 
                  borderRadius: "3px",
                  fontSize: "0.7rem"
                }}>
                  <span style={{ fontSize: "0.6rem", color: "#666", textTransform: "uppercase" }}>Adresse</span>
                  <div style={{ fontSize: "0.7rem", color: "#333", fontWeight: 500, marginTop: "1px" }}>
                    {address.value.address}
                  </div>
                </Box>
                
                <Box sx={{ 
                  display: "flex",
                  gap: 0.5
                }}>
                  <Box sx={{ 
                    flex: 1,
                    p: 0.5, 
                    backgroundColor: "#f8f9fa", 
                    borderRadius: "3px",
                    fontSize: "0.7rem"
                  }}>
                    <span style={{ fontSize: "0.6rem", color: "#666", textTransform: "uppercase" }}>Code Postal / Insee</span>
                    <div style={{ fontSize: "0.7rem", color: "#333", fontWeight: 500, marginTop: "1px" }}>
                      {address.value.inseePostalCodeData?.properties?.postal_code} / {address.value.inseePostalCodeData?.properties?.insee_com}
                    </div>
                  </Box>
                  
                  <Box sx={{ 
                    flex: 1,
                    p: 0.5, 
                    backgroundColor: "#f8f9fa", 
                    borderRadius: "3px",
                    fontSize: "0.7rem"
                  }}>
                    <span style={{ fontSize: "0.6rem", color: "#666", textTransform: "uppercase" }}>Coordonnées</span>
                    <div style={{ fontSize: "0.7rem", color: "#333", fontWeight: 500, marginTop: "1px" }}>
                      {address.value.latitude?.toFixed(6)}, {address.value.longitude?.toFixed(6)}
                    </div>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Export Button */}
          <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e0e0e0", paddingTop: "0", paddingBottom: "0" }}>
            <ButtonConfirm
              title="Export PDF"
              onClick={handleExportPdf}
              disabled={isExportDisable || exclusionCheck.status !== "done" || exclusionCheck.excluded}
              loading={isLoadingPDF}
              size="100%"
            />
          </Box>

          {/* Climate Risk Section */}
          <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>

            {/* Climate Risk List */}
            <ClimateRiskList
              climatRisk={climateRisk}
              rcpSelection={selectedRcp}
              riskSelected={selectedRisk}
              setRiskSelected={setSelectedRisk}
              getScaleLevel={getScaleLevel}
              onRcpChange={handleRcpChange}
            />
            
          </Box>

          {/* AI Model Selection */}
          <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", fontSize: "0.8rem" }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
              Modèles IA
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.8rem" }}>
              Choisissez les algos D4 Home pour lesquels vous souhaitez un
              résultat immédiat lors de la recherche
            </Typography>
            
            {[
              { key: "pool", label: "Détection de piscine", icon: "pool" },
              { key: "solarPanel", label: "Détection de panneaux solaires", icon: "solarPanel" },
              { key: "graffiti", label: "Détection de graffitis", icon: "graffiti" },
              { key: "walledWindow", label: "Détection de fenêtre murées", icon: "window" },
            ].map((model) => (
              <Box key={model.key} sx={{ display: "flex", alignItems: "center", mb: 1 }} className="ai-model-selection">
                <Icon containerStyle={{ flex: 1 }} type={model.icon} />
                <Typography 
                  sx={{ 
                    flex: 4, 
                    color: aiModelPermission[model.key as keyof typeof aiModelPermission] ? "black" : "grey", 
                    fontSize: "0.8rem"
                  }}
                >
                  {model.label}
                </Typography>
                <Checkbox
                  name={model.key}
                  onChange={handleCheckboxChange}
                  checked={aiDetectionSelectionState[model.key]}
                  disabled={!aiModelPermission[model.key as keyof typeof aiModelPermission]}
                />
              </Box>
            ))}
          </Box>

          {/* Build Number */}
          <div
            className="BuildNumber"
            style={{ padding: "10px", textAlign: "center" }}
          >
            Version: {process.env.REACT_APP_APP_VERSION} - {process.env.REACT_APP_BUILD_NUMBER}
          </div>
        </Box>
      </Box>

      {/* Right Zone - Map and Data */}
      <Box
        sx={{
          flex: 1,
          marginTop: "64px",
          height: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Map Container - Top */}
        <Box 
          className="map-wrapper"
          sx={{ 
            flex: 2,
            position: "relative",
            minHeight: 0, // Important pour flex
            overflow: "hidden", // Empêche l'espace gris
          }}
        >
          <OLMap
            ref={mapRef}
            style={{ width: "100%", height: "100%" }}
            selectedRisk={selectedRisk?.id}
            selectedRcp={selectedRcp}
            center={mapCenter}
            zoom={mapZoom}
            onLayersClick={() => {}}
            onInfoClick={() => {}}
            address={address.value?.address}
            lat={mapCenter.lat}
            lng={mapCenter.lng}
            data4HomeInfo={{
              buildings: address.value?.buildings,
              parcels: address.value?.parcels
            }}
            placeCoproData={
              selectedTab === COPRO_TAB_INDEX ? address.value?.placeCopro ?? null : null
            }
            placeCoproSelectedIndex={
              selectedTab === COPRO_TAB_INDEX ? selectedCoproIndex : 0
            }
            mapDataMode={selectedTab === COPRO_TAB_INDEX ? "placeCopro" : "place"}
            portfolioPoints={
              selectedTab === COPRO_TAB_INDEX && portfolioOpen ? portfolioPoints : null
            }
            portfolioSelectedId={
              selectedTab === COPRO_TAB_INDEX && portfolioOpen ? portfolioSelectedId : null
            }
            heavyRain={climateRisk.value?.risks['heavyRainObservations']}
            trees={trees.value}
            waterWays={waterWays.value}
            railRoads={railRoads.value}
            historicMonuments={historicMonuments.value}
            fireStations={fireStations.value}
            gasStations={gasStations.value}
            zonesSurveillees={address.value?.nearZone}
            arretePerils={arretePerils?.value?.features ?? []}
            onCoordinatesSelected={handleCoordinatesSelected}
          />
        </Box>

        {/* Data Tabs - Bottom */}
        <Box
          sx={{
            height: "500px", // Hauteur fixe
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "white",
            overflow: "hidden", // Empêche le débordement du conteneur
          }}
        >
          <DataTabs 
            address={address.value}
            selectedRisk={selectedRisk}
            climateRisk={climateRisk}
            userInfo={userInfo}
            onTabChange={setSelectedTab}
            selectedTab={selectedTab}
            onHistoricMonumentClick={handleHistoricMonumentClick}
            onZoneSurveilleeClick={handleZoneSurveilleeClick}
            onFireStationClick={handleFireStationClick}
            onGasStationClick={handleGasStationClick}
            arretePerils={arretePerils}
            selectedCoproIndex={selectedCoproIndex}
            onSelectedCoproIndexChange={setSelectedCoproIndex}
            onViewPortfolio={handleViewPortfolio}
          />
        </Box>
        {portfolioRequest && (
          <CoproPortfolioDrawer
            open={portfolioOpen}
            request={portfolioRequest}
            onClose={closePortfolioDrawer}
            onMapStateChange={handlePortfolioMapState}
            onOpenAddress={handleCoordinatesSelected}
          />
        )}
      </Box>
    
      {/* <Box sx={{
        width: "350px",
      }}></Box> */}
    </Box>
  );
}
